import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {IPresentation} from '../../../incase-domain/presentation';
import {IConnection} from '../../../incase-domain/connection';
import {IPaginatedResultWrapper} from '../../../incase-domain/search';
import {useDeletePresentations} from '../../hooks/usePresentationDeletion';
import SendSharePresentationModal from './SendSharePresentationModal';
import ErrorModalWrapper from '../../../incase-domain/components/modals/errorModal/ErrorModalWrapper';
import ErrorModalIcon from '../../../incase-domain/components/modals/errorModal/ErrorModalIcon';
import ErrorModalContent from '../../../incase-domain/components/modals/errorModal/ErrorModalContent';
import ErrorModalCloseButton from '../../../incase-domain/components/modals/errorModal/ErrorModalCloseButton';
import CreateRoomModal from '../../pages/rooms/createRoom/CreateRoomModal';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';

interface PresentationListModalProps {
    deletePresentations?: boolean;
    setDeletePresentations?: React.Dispatch<React.SetStateAction<boolean>>;
    selected?: IPresentation[];
    setSelected?: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    presentationToDelete?: IPresentation[];
    setPresentationToDelete?: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    sendPresentationModal?: boolean;
    setSendPresentationModal?: React.Dispatch<React.SetStateAction<boolean>>;
    connections?: IPaginatedResultWrapper<IConnection>;
    onSearch?: (searchTerm: string) => void;
    onDeletePageChangeHandler?: (deletedItemCount: number) => void;
    selectedForRoomCreation?: IPresentation | undefined;
    setSelectedForRoomCreation?: React.Dispatch<React.SetStateAction<IPresentation | undefined>>;
}

function PresentationListModals(props: PresentationListModalProps): JSX.Element {
    const {
        onSearch,
        onDeletePageChangeHandler,
        connections,
        setDeletePresentations,
        sendPresentationModal,
        setSendPresentationModal,
        setSelected,
        setPresentationToDelete,
        presentationToDelete,
        deletePresentations,
        selected,
        selectedForRoomCreation,
        setSelectedForRoomCreation,
    } = props;
    const {t} = useTranslation();
    const client = useQueryClient();
    const [openErrorModal, setOpenErrorModal] = useState<{isOpen: boolean; message: string}>({isOpen: false, message: ''});
    const deletePresentation = useDeletePresentations(setPresentationToDelete, setDeletePresentations, setOpenErrorModal);
    const deletePresentationsMutation = useDeletePresentations(setSelected, setDeletePresentations, setOpenErrorModal);

    function deletePresSingle(): void {
        presentationToDelete &&
            onDeletePageChangeHandler &&
            presentationToDelete &&
            deletePresentation.mutate(presentationToDelete, {
                onSuccess: () => {
                    onDeletePageChangeHandler(1);
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()});
                },
            });
    }

    function deletePresMulti(): void {
        selected &&
            onDeletePageChangeHandler &&
            deletePresentationsMutation.mutate(selected, {
                onSuccess: () => {
                    onDeletePageChangeHandler(selected.length);
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()});
                },
            });
    }

    return (
        <>
            <ConfirmModal
                message={
                    (selected && selected.length === 1) || (presentationToDelete && presentationToDelete?.length)
                        ? t('editorPage.deletePresentationModal.deleteSelectedPresentationConfirmation')
                        : t('editorPage.deletePresentationModal.deleteSelectedPresentationsConfirmation')
                }
                color="error"
                isOpen={deletePresentations || false}
                onCancel={() => setDeletePresentations && setDeletePresentations(false)}
                onConfirm={presentationToDelete && presentationToDelete?.length > 0 ? deletePresSingle : deletePresMulti}
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
            />
            <ErrorModalWrapper isOpen={openErrorModal.isOpen}>
                <ErrorModalIcon />
                <ErrorModalContent title={t('homePage.errors.presentationDeleteError')} description={openErrorModal.message} />
                <ErrorModalCloseButton onClose={() => setOpenErrorModal({isOpen: false, message: ''})} />
            </ErrorModalWrapper>
            <SendSharePresentationModal
                sendPresentationModal={!!sendPresentationModal}
                selected={selected}
                setSendPresentationModal={setSendPresentationModal}
                setSelected={setSelected}
                onSearch={onSearch}
                connections={connections}
            />
            <CreateRoomModal
                open={!!selectedForRoomCreation}
                setOpen={() => setSelectedForRoomCreation && setSelectedForRoomCreation(undefined)}
                presentation={selectedForRoomCreation}
            />
        </>
    );
}

export default PresentationListModals;
